import { DicomMetadataStore } from '@ohif/core';
import { apiUrl } from '../../UploadDocument/apiUrl';

export default async function sendDicomAut(studyInstanceUid) {
  try {
    // Obtener el servidor por defecto
    const server = await getServer();

    // Si no se encuentra un servidor por defecto, salimos
    if (!server) {
      console.log('No se encontró un servidor por defecto');
      return;
    }

    console.log('Server encontrado:', server);

    // Enviar DICOM al servidor
    const dicomResponse = await sendDicomToServer(server, studyInstanceUid);

    if (dicomResponse) {
      console.log('Respuesta del servidor DICOM:', dicomResponse);
    } else {
      console.log('No se recibió una respuesta válida del servidor DICOM');
    }
  } catch (error) {
    console.error('Error al enviar DICOM:', error);
  }
}

// Obtener el servidor por defecto
async function getServer() {
  try {
    const result = await fetch(`${apiUrl}/api/server`);

    // Manejo de respuesta 304
    if (result.status === 304) {
      console.log('La respuesta no ha cambiado desde la última vez');
      return null;
    }

    // Verificación de éxito
    if (result.status !== 200) {
      throw new Error('Failed to fetch server info');
    }

    const res = await result.json();

    // Filtrar servidor por defecto
    const filteredData = res.filter(item => item.isDefault === true);

    if (filteredData.length === 0) {
      console.log('No se encontraron servidores con isDefault: true');
      return null;
    }

    // Realiza la solicitud POST al backend para ver si hay conexión
    const pingResponse = await fetch(apiUrl + '/api/dicom/ping', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        host: filteredData[0].host,
        port: filteredData[0].port,
        calledAET: filteredData[0].calledAET,
        callingAET: filteredData[0].callingAET,
      }),
    });

    const pingData = await pingResponse.json();
    console.log('Ping Response:', pingData);

    if (pingResponse.status !== 200) {
      console.error('Error de conexión con el servidor:', pingData);
      return null; // Si el ping falla, retornamos null
    }

    // Retornar el primer servidor con isDefault: true si todo está correcto
    return filteredData[0];
  } catch (error) {
    console.error('Error al obtener o procesar los datos del servidor:', error);
    return null; // Devolver null si hay un error
  }
}

// Enviar DICOM al servidor
async function sendDicomToServer(server, studyInstanceUid) {
  try {
    const { host, port, calledAET, callingAET } = server; // Desestructuración del objeto `server`

    const response = await fetch(
      `${apiUrl}/api/dicom/find?host=${host}&port=${port}&calledAET=${calledAET}&callingAET=${callingAET}&studyInstanceUid=${studyInstanceUid}`
    );

    // Manejo de respuesta 304
    if (response.status === 304) {
      console.log('La respuesta no ha cambiado desde la última vez');
      return null;
    }

    // Si la respuesta es 404, logueamos el studyInstanceUid
    if (response.status === 404) {
      console.log(`Error 404 al buscar DICOM para el studyInstanceUid: ${studyInstanceUid}`);

      // Intentar obtener metadatos del estudio desde DicomMetadataStore
      const studyMetadata = DicomMetadataStore.getStudy(studyInstanceUid);
      enviarTodasLasImágenes(studyMetadata, server);
      // Retornar un objeto para indicar que hubo un error con el 404
      return { message: 'Enviando Dicom', studyInstanceUid };
    }

    // Verificación de éxito
    if (response.status !== 200) {
      throw new Error(`Failed to send DICOM to server: ${response.status}`);
    }

    const res = await response.json();
    return res; // Retornar la respuesta si todo salió bien
  } catch (error) {
    console.error(
      `Error al enviar DICOM al servidor para el studyInstanceUid ${studyInstanceUid}:`,
      error
    );
    return null; // Devolver null en caso de error
  }
}

const failedUrls = new Set<string>();
const sendImageToBackend = async (
  image: {
    url: string;
    fileName: string;
    seriesUID: string;
  },
  server: any
): Promise<void> => {
  const { url, fileName, seriesUID } = image;
  const { host, port, calledAET, callingAET } = server;
  try {
    const response = await fetch(apiUrl + '/api/dicom/upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        dicomFile: url,
        host,
        port,
        calledAET,
        callingAET: callingAET || 'MAYU',
      }),
    });

    if (!response.ok) {
      console.error(`Error al enviar imagen ${fileName}:`, response.statusText);
      failedUrls.add(url);
    }
  } catch (error) {
    console.error(`Error enviando imagen ${fileName}:`, error);
    failedUrls.add(url);
  }
};

const enviarTodasLasImágenes = async (studyMeta, server) => {
  try {
    if (!studyMeta) {
      console.error('No se encontró el estudio');
      return;
    }

    const studyUID = studyMeta.StudyInstanceUID;

    const imageUrls: { url: string; fileName: string; seriesUID: string }[] = [];

    for (const series of studyMeta.series) {
      const seriesUID = series.SeriesInstanceUID;
      for (const instance of series.instances) {
        const sopUID = instance.SOPInstanceUID;
        const rootUrl = instance.wadoRoot;

        const match = rootUrl.match(/^(https?:\/\/[^\/]+)/);
        if (match && studyUID && seriesUID && sopUID) {
          const wadoUrl =
            `${match[0]}/dcm4chee-arc/aets/MEDVIEWER/wado` +
            `?requestType=WADO&studyUID=${studyUID}&seriesUID=${seriesUID}&objectUID=${sopUID}&contentType=application/dicom`;

          const fileName = `DICOM_${seriesUID}_${sopUID}.dcm`;
          imageUrls.push({ url: wadoUrl, fileName, seriesUID });
        }
      }
    }

    if (imageUrls.length === 0) {
      console.error('No se encontraron imágenes para enviar');
      return;
    }

    const totalImages = imageUrls.length;
    const counter = { loaded: 0 };

    console.log(`Enviando ${totalImages} imágenes al servidor DICOM...`, { duration: 6000 });

    const runWorker = async () => {
      while (imageUrls.length > 0) {
        const item = imageUrls.shift();
        if (!item || failedUrls.has(item.url)) {
          continue;
        }

        await sendImageToBackend(item, server);

        counter.loaded++;

        await new Promise(res => setTimeout(res, 200)); // Allow the UI to update
      }
    };

    const workers = Array.from({ length: 10 }, runWorker);
    await Promise.all(workers);

    console.log('Imágenes enviadas correctamente');
  } catch (err) {
    console.error('Error al enviar imágenes:', err);
  }
};
